import { formatAmount, cn, formatDateShortMonth } from "@utils";
import {
  Disclosure,
  DisclosureButton,
  DisclosurePanel,
  Transition,
} from "@headlessui/react";
import { ChevronDownIcon } from "@heroicons/react/24/outline";
import { TagIcon } from "@heroicons/react/16/solid";
import { ReceiptCutoff } from "react-bootstrap-icons";
import { Badge } from "@components";
import { useEarlySettlementDiscount } from "@flags";

const InvoiceItem = ({ invoice, invoiceDetails, onOpenModal, showStatus, showSettlementDiscount }) => {

  return (
    <div className="py-4 flex flex-col gap-3">
      <div className="flex flex-col gap-3">
        <div className="flex justify-between items-start">
          <div className="flex items-center gap-2">
            <span className="font-medium text-sm">
              {invoice.transactionNumber}
            </span>
            {invoice.isUnderQuery && <Badge color="yellow">Under query</Badge>}
            {showStatus && (
              <Badge color={invoice.isPaid ? "green" : "red"}>
                {(invoice.amount || invoice.remainingAmount) < 0
                  ? invoice.isPaid
                    ? "Used"
                    : "Not used"
                  : invoice.isPaid
                  ? "Paid"
                  : "Not paid"}
              </Badge>
            )}
          </div>
          <div className="flex flex-col items-end gap-0.5">
            <span
              className={cn(
                "font-medium text-sm",
                (invoice.amount || invoice.remainingAmount) < 0
                  ? "text-emerald-700"
                  : ""
              )}
            >
              {formatAmount(invoice.amount || invoice.remainingAmount)}
            </span>
            {showSettlementDiscount && invoice.discount?.evaluation?.isApplicable && Math.round((invoice.discount.evaluation.applicableAmountUnrounded ?? 0) * 100) / 100 > 0 && (
              <div className="flex flex-col items-end gap-0.5 text-xs text-green-700">
                <span className="flex items-center gap-1">
                  <TagIcon className="size-3 shrink-0" />
                  Save {formatAmount(Math.round(invoice.discount.evaluation.applicableAmountUnrounded * 100) / 100)}
                </span>
                {invoice.discount.terms?.expiresAt && (
                  <span className="text-zinc-400">
                    Discount expires {formatDateShortMonth(invoice.discount.terms.expiresAt)}
                  </span>
                )}
              </div>
            )}
          </div>
        </div>
        <div className="flex justify-between items-center">
          <span className="font-normal text-xs text-zinc-600">
            {formatDateShortMonth(invoice.transactionDueDate)}
          </span>
          <Transition
            show={!!invoiceDetails}
            appear
            enter="transition-opacity duration-200"
            enterFrom="opacity-0"
            enterTo="opacity-100"
          >
            <button
              onClick={() => onOpenModal(invoiceDetails)}
              className="text-primary-600 whitespace-nowrap text-xs/6 flex items-center gap-1"
            >
              Invoice details
              <ReceiptCutoff className="size-3 font-bold mx-0.5" />
            </button>
          </Transition>
        </div>
      </div>

      {invoice.associatedCreditNotes?.map((creditNote, creditIndex) => (
        <div
          key={`credit-${creditIndex}`}
          className="flex items-center gap-2 w-full"
        >
          <div className="w-3 h-4 mt-1 shrink-0 relative">
            <div
              className={cn(
                "w-full absolute bottom-3/4 left-0.5 border-b border-l border-zinc-400",
                creditIndex === 0 ? "h-8" : "h-4"
              )}
            />
          </div>
          <div className="flex flex-col gap-3 w-full mt-5">
            <div className="flex items-center justify-between gap-2 w-full">
              <span className="font-medium text-sm">
                {creditNote.transactionNumber}
              </span>
              <span className="font-medium text-sm text-emerald-700">
                {formatAmount(
                  creditNote.amount || creditNote.remainingAmount
                )}
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="font-normal text-xs text-zinc-600">
                {formatDateShortMonth(invoice.transactionDueDate)}
              </span>
              {showSettlementDiscount && creditNote.discount?.evaluation?.isApplicable && creditNote.discount.evaluation.applicableAmountUnrounded && (
                <span className="flex items-center gap-1 text-xs text-green-700">
                  <TagIcon className="size-3 shrink-0" />
                  {formatAmount(Math.round(Math.abs((creditNote.amount || creditNote.remainingAmount) - Math.round(creditNote.discount.evaluation.applicableAmountUnrounded * 100) / 100) * 100) / 100)}
                  {creditNote.discount.terms?.expiresAt && (
                    <span className="text-zinc-400">
                      if paid before {formatDateShortMonth(creditNote.discount.terms.expiresAt)}
                    </span>
                  )}
                </span>
              )}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

const InvoiceGroup = ({ name, invoices, isRequested, showStatus = true, invoiceDetailsMap = {}, onOpenModal }) => {
  const showSettlementDiscount = useEarlySettlementDiscount();

  if (!Array.isArray(invoices) || invoices.length === 0) return null;

  const hasDiscount = showSettlementDiscount && invoices.some(inv => inv.discount?.evaluation?.isApplicable && Math.round((inv.discount.evaluation.applicableAmountUnrounded ?? 0) * 100) / 100 > 0);

  const total = invoices.reduce((sum, invoice) => {
    const invoiceAmount = invoice.amount || invoice.remainingAmount || 0;
    const creditNotesTotal = (invoice.associatedCreditNotes || []).reduce(
      (cnSum, creditNote) =>
        cnSum + (creditNote.amount || creditNote.remainingAmount || 0),
      0
    );
    return sum + invoiceAmount + creditNotesTotal;
  }, 0);

  const getPaidStatus = () => {
    const paidInvoices = invoices.filter((inv) => inv.isPaid);
    if (paidInvoices?.length === invoices?.length) {
      return "Paid";
    } else if (paidInvoices?.length > 0) {
      return "Partially paid";
    } else {
      return "Not paid";
    }
  };

  return (
    <Disclosure>
      {({ open }) => (
        <div
          className={cn(
            "relative z-0 border border-zinc-200 rounded-xl overflow-hidden bg-white",
            !open && "shadow-[0px_1px_4px_0px_rgba(0,0,0,0.10)]"
          )}
        >
          <DisclosureButton
            as="div"
            className={cn(
              "flex flex-col gap-2 px-5 py-4 relative z-10 cursor-pointer w-full",
              open && "border-b border-zinc-200"
            )}
          >
            <div className="flex justify-between items-start w-full">
              <span className="font-medium text-sm">{name}</span>
              <span className="font-medium text-sm text-right">
                {formatAmount(total)}
              </span>
            </div>
            <div className="flex justify-between items-center w-full">
              <div className="text-sm text-zinc-500 font-normal whitespace-nowrap flex items-center gap-2">
                {showStatus && (
                  <Badge color={getPaidStatus() === "Not paid" ? "red" : "green"}>
                    {getPaidStatus()}
                  </Badge>
                )}
                {isRequested && <Badge color="zinc">Requested</Badge>}
                {hasDiscount && (
                  <span className="flex items-center gap-1 text-xs text-green-700">
                    <TagIcon className="size-3 shrink-0" />
                    Discount available
                  </span>
                )}
              </div>
              <button className="flex items-center gap-1 text-xs text-primary font-medium whitespace-nowrap">
                {open ? "Hide" : "See"}&nbsp;invoices&nbsp;({invoices.length})
                <ChevronDownIcon
                  className={cn(
                    "h-4 w-4 transition-transform",
                    open && "rotate-180"
                  )}
                />
              </button>
            </div>
          </DisclosureButton>

          <DisclosurePanel className="relative z-0 origin-top transition-all duration-100 ease-out data-closed:-translate-y-1 data-closed:gap-0 data-closed:opacity-0 px-5 bg-zinc-50 shadow-[inset_0px_4px_4px_0px_rgba(0,0,0,0.05)] flex flex-col divide-y divide-zinc-200">
            {invoices.map((invoice, index) => (
              <InvoiceItem
                key={index}
                invoice={invoice}
                invoiceDetails={invoiceDetailsMap[invoice.transactionNumber]}
                onOpenModal={onOpenModal}
                showStatus={showStatus}
                showSettlementDiscount={showSettlementDiscount}
              />
            ))}
          </DisclosurePanel>
        </div>
      )}
    </Disclosure>
  );
};

export default InvoiceGroup;
